import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { SortableContainer } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';
import { Link } from 'react-router-dom';

import { useAbility } from '@casl/react';

import ActionBar from '../../../../common/components/actionBar/ActionBar';
import PollTeaser from './PollTeaser';
import SvgIcon from '../../../../common/components/SvgIcon';
import { SEGMENTATION } from '../../../../common/constants/RouteConstants';
import { VIEW, SURVEY_BUILDER, SURVEY_BUILDER_INTERNAL } from '../../../../common/ability';
import { Can, AbilityContext } from '../../../../common/Can';

function PollContainer(props) {
  const { items, allowDrag } = props;
  return (
    <div className='c-reorder-list c-reorder-list--content'>
      {items.map((poll, index) => (
        <PollTeaser key={poll._id} index={index} poll={poll} disabled={!allowDrag} />
      ))}
    </div>
  );
}

PollContainer.propTypes = {
  items: PropTypes.array.isRequired,
};

function PollsList(props) {
  const { Store, SegmentationStore, ProjectStore, match, history } = props;

  const [allowDrag, setAllowDrag] = useState(true);
  const ability = useAbility(AbilityContext);

  const projectId = match.params.id;

  useEffect(() => {
    if (Store && SegmentationStore && projectId) {
      Store.load(projectId);
      SegmentationStore.load(projectId);
    }
  }, [Store, SegmentationStore, ProjectStore.project, projectId]);

  function addNew() {
    history.push(`surveys/create-survey`);
  }

  // AI survey builder (sdesregistry/IH-Engage#2529): a separate SPA on the same host —
  // full-page navigation, same tab; the shared GMV_COOKIE carries the login across
  // (cookies ignore ports, so it also reaches the Vite dev server in local dev, where
  // REACT_APP_SURVEY_BUILDER_URL points the entry at http://localhost:3050 — the relative
  // path only exists on built/deployed hosts).
  function openSurveyBuilder() {
    const base = process.env.REACT_APP_SURVEY_BUILDER_URL || '';
    // GMV-team staff get the builder's internal review affordances (#2532)
    const internal = ability.can(VIEW, SURVEY_BUILDER_INTERNAL) ? '&internal=true' : '';
    // the display name feeds the builder's back-to-project breadcrumb (#2532)
    const projectName = ProjectStore.project?.projectName;
    const name = projectName ? `&projectName=${encodeURIComponent(projectName)}` : '';
    window.location.assign(`${base}/survey-builder/?projectId=${projectId}${internal}${name}`);
  }
  const canUseSurveyBuilder = ability.can(VIEW, SURVEY_BUILDER);

  function onSortEnd({ oldIndex, newIndex }) {
    setAllowDrag(false);
    const newOrder = arrayMoveImmutable(Store.polls, oldIndex, newIndex);
    Store.update(match.params.id, newOrder).then(() => {
      setAllowDrag(true);
    });
  }

  return (
    <div className='l-main'>
      <ActionBar
        addNew={addNew}
        label='Surveys'
        hasButton={true}
        buttonLabel='Add'
        secondaryAction={canUseSurveyBuilder ? openSurveyBuilder : undefined}
        secondaryLabel='Create with AI'
        secondaryIcon='star-filled'
      />
      <Can I={VIEW} a={SEGMENTATION}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {SegmentationStore.activeQuestion ? (
          <div className='c-message c-message--icon is-success'>
            <SvgIcon icon='poll-segment' />
            <p className='c-message__description'>
              {`Segmentation: `}
              <strong>{SegmentationStore.activeQuestion.questionText}</strong>
            </p>
          </div>
        ) : Store.polls.length === 0 ? null : (
          <div className='c-message c-message--icon is-information'>
            <SvgIcon icon='poll-segment' />
            <p className='c-message__description'>
              {`Currently there is not a segmentation question active. `}
              <Link to={SEGMENTATION}>Add segmentation question</Link>
            </p>
          </div>
        )}
      </Can>
      <SortableList items={Store.polls} onSortEnd={onSortEnd} useDragHandle={true} allowDrag={allowDrag} />
    </div>
  );
}

const SortableList = SortableContainer((props) => {
  return <PollContainer {...props} />;
});

PollsList.propTypes = {
  Store: PropTypes.object.isRequired,
  SegmentationStore: PropTypes.object.isRequired,
  ProjectStore: PropTypes.object.isRequired,
};

export default inject((root) => ({
  Store: root.RootStore.pollsListStore,
  SegmentationStore: root.RootStore.segmentationStore,
  ProjectStore: root.RootStore.projectStore,
}))(observer(PollsList));
